"use client";

import { useId, useState } from "react";
import { Icon } from "./icon";

/**
 * Profile v1 re-skin — expandable bio CLIENT leaf for the About card.
 *
 * Receives ALREADY-SANITIZED HTML from the server parent
 * (`profile-about.tsx` runs `sanitize-html` + `ABOUT_SANITIZE_OPTIONS` before
 * handing the string over) — this component must NEVER receive raw
 * agent-authored input.
 *
 * Full sanitized content is always in the server-rendered body. Long bios
 * collapse visually to four lines; no content is deferred into an RSC script
 * or inserted on expansion. Focus on a bio link expands it so keyboard users
 * never land on a clipped link.
 */

export function AboutBio({ html }: { html: string }) {
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();

  const plainText = html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const isLong = plainText.length > 280;

  const textStyle = { color: "var(--brand-text)", fontSize: "15px" } as const;

  return (
    <div className="mt-4">
      <div
        id={contentId}
        className={`ad-prose leading-relaxed${isLong && !expanded ? " line-clamp-4" : ""}`}
        style={textStyle}
        onFocusCapture={() => setExpanded(true)}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {isLong && (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => setExpanded((value) => !value)}
          className="mt-2 inline-flex items-center gap-1 font-medium"
          style={{ color: "var(--brand-primary-text, var(--brand-primary))", fontSize: "14px" }}
        >
          {expanded ? "Show less" : "Read more"}
          {/* `expand_more`/`expand_less` are absent from the installed Material
              Symbols subset — `keyboard_arrow_*` are the validated equivalents. */}
          <Icon
            name={expanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}
            size={16}
          />
        </button>
      )}
    </div>
  );
}
